/**
 * Channel List Component
 * Pure data/selection layer for live channels - loads channels, tracks
 * favorites/hidden state, and drives channel selection/playback. Used by
 * EpgGuide.js (the schedule grid is the only channel-browsing UI now -
 * this class used to also render a sidebar list, removed when Live TV was
 * folded entirely into the TV Guide page) and VideoPlayer.js (channel-up/
 * down surfing).
 */
class ChannelList {
    constructor() {
        this.channels = [];
        this.groups = [];
        this.hiddenItems = new Set(); // Set<"type:sourceId:itemId">
        this.visibleFavorites = new Set(); // Set<"sourceId:channelId">
        this.currentChannel = null;
        this.sources = [];
        this.isLoading = false;
        // Incremented at the top of every selectChannel() call; a call whose
        // async getStreamUrl() resolves after a newer selection has already
        // been made detects it's stale (via the check below) and never
        // reaches player.play() at all - otherwise a fast double-click/
        // channel-surf could let an earlier click's slower network response
        // hijack playback back to it after a later click already started
        // playing the right channel.
        this.channelSelectionGeneration = 0;
    }

    /**
     * Load available sources
     */
    async loadSources() {
        try {
            this.sources = await API.sources.getAll();
            console.log('[ChannelList] loadSources: Got', this.sources?.length || 0, 'sources');
        } catch (err) {
            console.error('Error loading sources:', err);
        }
    }

    /**
     * Load channels from all enabled sources (there's no per-source filter
     * UI anymore - the TV Guide always shows every enabled source's
     * channels together).
     *
     * De-duped via a shared in-flight promise (not just an `isLoading`
     * guard that returns immediately) - Home's dashboard load and a direct
     * Guide navigation can both call this around boot, and a caller that
     * "loses" the race needs to actually wait for the winner's result
     * rather than resolve immediately against a still-empty this.channels.
     */
    async loadChannels() {
        if (this._loadPromise) return this._loadPromise;
        this.isLoading = true;
        this._loadPromise = this.loadAllChannels();
        try {
            await this._loadPromise;
        } finally {
            this.isLoading = false;
            this._loadPromise = null;
        }
    }

    /**
     * Load channels from all enabled sources
     */
    async loadAllChannels() {
        try {
            const xtreamSources = this.sources.filter(s => s.type === 'xtream' && s.enabled);
            const m3uSources = this.sources.filter(s => s.type === 'm3u' && s.enabled);
            console.log('[ChannelList] loadAllChannels: xtream=', xtreamSources.length, 'm3u=', m3uSources.length);

            // Every source loads independently and in parallel (each returns
            // its own {groups, channels} rather than mutating this.channels/
            // this.groups directly) - safe to combine afterward regardless of
            // how many sources are configured, and doesn't serialize sources
            // behind each other for no reason.
            const results = await Promise.all([
                ...xtreamSources.map(source => this.loadChannelsForSource(source.id, 'xtream')),
                ...m3uSources.map(source => this.loadChannelsForSource(source.id, 'm3u')),
            ]);

            this.groups = [];
            this.channels = [];
            results.forEach(({ groups, channels }) => {
                this.groups = this.groups.concat(groups);
                this.channels = this.channels.concat(channels);
            });

            await Promise.all([
                this.loadHiddenItems(),
                this.loadFavorites()
            ]);
            // prewarmFavoriteChannelProbes() disabled 2026-08-12: this
            // account's IPTV line only allows 1 concurrent connection. The
            // prewarm loop opens a real provider connection per favorite
            // channel shortly after every login/channel-list load - on a
            // multi-connection line that's a free latency win, but here it
            // repeatedly steals the account's only slot right when the user
            // is most likely to also be starting something else, causing
            // "video source unavailable" (provider HTTP 551, max connections)
            // for several minutes after every login. Not called.
        } catch (err) {
            console.error('Error loading all channels:', err);
        }
    }

    /**
     * Background, best-effort pre-warm of the server's probe cache (see
     * probe.js) for favorite channels. "Auto Transcode" (the default setting)
     * runs a real ffprobe subprocess + network round-trip to the provider on
     * every channel switch unless that URL was already probed recently -
     * doing this quietly in the background for the channels someone actually
     * watches means the probe is usually already warm by the time they click
     * it, shaving that latency off entirely for the common case. Silent,
     * best-effort, and cheap to call repeatedly: probe.js's own 24h cache
     * means only the first call per channel per day does real work.
     */
    async prewarmFavoriteChannelProbes() {
        if (this._prewarmingFavorites) return;

        const favoriteChannels = this.channels.filter(c =>
            c.sourceType === 'xtream' && this.isFavorite(c.sourceId, c.id)
        );
        if (favoriteChannels.length === 0) return;

        this._prewarmingFavorites = true;
        // Let the page settle first - this is low-priority background work.
        await new Promise(resolve => setTimeout(resolve, 3000));

        try {
            const streamFormat = window.app?.player?.settings?.streamFormat || 'm3u8';
            for (const channel of favoriteChannels) {
                // Each probe spawns an ffprobe subprocess and opens a provider
                // connection. If the user is on the Watch page they're playing
                // VOD - very possibly a heavy (software) transcode on this
                // GPU-less box - and a stream of ~1 probe/sec steals exactly
                // the CPU/upstream-bandwidth that transcode needs, badly
                // delaying playback start. This prewarm only ever benefits
                // *live* channel switching, so bail out entirely while VOD is
                // on screen; it'll re-run next time the channel list loads.
                if (window.app?.currentPage === 'watch') break;
                try {
                    const result = await API.proxy.xtream.getStreamUrl(channel.sourceId, channel.streamId, 'live', streamFormat);
                    if (result?.url) {
                        await fetch(`/api/probe?url=${encodeURIComponent(result.url)}`);
                    }
                } catch (e) {
                    // Best-effort - one channel failing to prewarm shouldn't stop the rest
                }
                // Stagger so we don't spawn a burst of ffprobe processes at once
                await new Promise(resolve => setTimeout(resolve, 800));
            }
        } finally {
            this._prewarmingFavorites = false;
        }
    }

    /**
     * Load channels for one source, Xtream or M3U (both use the same
     * unified Xtream-style API endpoints - backend supports both source
     * types). Returns {groups, channels} rather than mutating
     * this.groups/this.channels directly, so loadAllChannels() can safely
     * load every source in parallel and combine results afterward instead
     * of serializing sources behind each other.
     */
    async loadChannelsForSource(sourceId, sourceType) {
        // Categories and streams don't depend on each other - fetch both at
        // once instead of one-after-another (was costing a full extra
        // network round trip on every load, worse on higher-latency
        // connections).
        const [categories, streams] = await Promise.all([
            API.proxy.xtream.liveCategories(sourceId),
            API.proxy.xtream.liveStreams(sourceId)
        ]);

        // Build the category lookup once instead of re-scanning the full
        // categories array for every single stream (categories.find(...)
        // inside .map() below was O(streams x categories) - on a catalog
        // with tens of thousands of streams this was real, measurable CPU
        // time, worse still on a slower mobile CPU).
        const categoryNameById = new Map(
            categories.map(cat => [String(cat.category_id), cat.category_name])
        );

        const groups = categories.map(cat => ({
            id: `${sourceType}_${sourceId}_${cat.category_id}`,
            name: cat.category_name,
            sourceId,
            sourceType
        }));

        const channels = streams.map(stream => ({
            id: `${sourceType}_${sourceId}_${stream.stream_id}`,
            streamId: stream.stream_id,
            name: stream.name,
            tvgId: stream.epg_channel_id,
            tvgLogo: stream.stream_icon,
            groupId: `${sourceType}_${sourceId}_${stream.category_id}`,
            groupTitle: categoryNameById.get(String(stream.category_id)) || 'Uncategorized',
            sourceId,
            sourceType,
            ...(sourceType === 'm3u'
                ? { url: stream.stream_url } // M3U has direct URLs
                : {
                    // Catch-up/timeshift availability (Xtream-only; tv_archive_duration is in days)
                    tvArchive: stream.tv_archive === 1 || stream.tv_archive === '1',
                    tvArchiveDuration: parseInt(stream.tv_archive_duration, 10) || 0
                })
        }));

        return { groups, channels };
    }

    /**
     * Load hidden items
     */
    async loadHiddenItems() {
        try {
            // Only live channels/groups are relevant here - scoping avoids
            // pulling hidden movies/series too (see server route comment,
            // can be a 200k+ row response unscoped).
            const items = await API.channels.getHidden(null, 'live');
            this.hiddenItems = new Set(items.map(i => `${i.item_type}:${i.source_id}:${i.item_id}`));
        } catch (err) {
            console.error('Error loading hidden items:', err);
        }
    }

    /**
     * Check if item is hidden
     */
    isHidden(type, sourceId, itemId) {
        return this.hiddenItems.has(`${type}:${sourceId}:${itemId}`);
    }

    /**
     * Load favorites
     */
    async loadFavorites() {
        try {
            // Get all favorites (filtered for channels or legacy items without type)
            const allFavs = await API.favorites.getAll();
            const channelFavs = allFavs.filter(f => !f.item_type || f.item_type === 'channel');

            this.visibleFavorites = new Set(
                channelFavs.map(f => `${f.source_id}:${f.item_id || f.channel_id}`)
            );
        } catch (err) {
            console.error('Error loading favorites:', err);
        }
    }

    /**
     * Check if channel is favorite
     */
    isFavorite(sourceId, channelId) {
        return this.visibleFavorites.has(`${sourceId}:${channelId}`);
    }

    /**
     * Sync favorite status from external source (e.g. EPG guide, the only
     * place favorites are toggled from now) without an API call.
     */
    syncFavorite(sourceId, channelId, isFavorite) {
        const key = `${sourceId}:${channelId}`;
        const currentlyFav = this.visibleFavorites.has(key);

        if (currentlyFav === isFavorite) return; // No change needed

        if (isFavorite) {
            this.visibleFavorites.add(key);
        } else {
            this.visibleFavorites.delete(key);
        }
    }

    /**
     * Select a channel by id and start playback.
     */
    async selectChannel(dataset) {
        let channel = this.channels.find(c => c.id === dataset.channelId);

        // Fallback: the channel may not be in the currently-loaded list - e.g.
        // a favorite launched from Home before the full catalog has loaded, or
        // a favorited channel that lives in a hidden category. The caller's
        // dataset carries everything play() needs (streamId/sourceType/url/
        // name), so build a channel object from it directly instead of bailing
        // out (which used to silently do nothing).
        if (!channel && (dataset.streamId || dataset.url)) {
            channel = {
                id: dataset.channelId,
                name: dataset.name || 'Channel',
                sourceId: dataset.sourceId,
                sourceType: dataset.sourceType,
                streamId: dataset.streamId || '',
                url: dataset.url || '',
                tvgLogo: dataset.tvgLogo || ''
            };
        }
        if (!channel) return;

        const mySelection = ++this.channelSelectionGeneration;
        this.currentChannel = channel;

        // Update the TV Guide topbar's "Now Playing" label - this is the
        // one chokepoint every play path funnels through (grid clicks,
        // channel-up/down surfing, Home's favorite-tile click), so this is
        // the only place that needs to do it.
        window.app?.updateGuideOverlayNowPlaying?.(channel.name);

        // Get stream URL
        let streamUrl;
        if (channel.sourceType === 'xtream') {
            // Get stream format from player settings (server-side) or fallback
            const streamFormat = window.app?.player?.settings?.streamFormat || 'm3u8';
            let result;
            try {
                result = await API.proxy.xtream.getStreamUrl(channel.sourceId, channel.streamId, 'live', streamFormat);
            } catch (err) {
                if (mySelection !== this.channelSelectionGeneration) return; // a newer channel selection has already taken over
                console.error('Failed to resolve stream URL:', err);
                window.app?.player?.showError?.('Failed to load this channel. Please try again.');
                return;
            }
            if (mySelection !== this.channelSelectionGeneration) return; // a newer channel selection has already taken over
            streamUrl = result.url;
        } else {
            streamUrl = channel.url;
        }

        // Play channel
        if (window.app?.player) {
            window.app.player.play(channel, streamUrl);
        }
    }

    /**
     * Select next channel in display (visible-channel) order
     */
    selectNextChannel() {
        if (!this.currentChannel) return;
        const channels = this.getVisibleChannels();
        if (channels.length === 0) return;

        const currentIndex = channels.findIndex(c =>
            c.id === this.currentChannel.id && c.sourceId === this.currentChannel.sourceId
        );
        if (currentIndex === -1) return;

        const nextChannel = channels[(currentIndex + 1) % channels.length];
        this.selectChannel({ channelId: nextChannel.id });
    }

    /**
     * Select previous channel in display (visible-channel) order
     */
    selectPrevChannel() {
        if (!this.currentChannel) return;
        const channels = this.getVisibleChannels();
        if (channels.length === 0) return;

        const currentIndex = channels.findIndex(c =>
            c.id === this.currentChannel.id && c.sourceId === this.currentChannel.sourceId
        );
        if (currentIndex === -1) return;

        const prevChannel = channels[(currentIndex - 1 + channels.length) % channels.length];
        this.selectChannel({ channelId: prevChannel.id });
    }

    /**
     * Get list of visible (non-hidden) channels in display order
     */
    getVisibleChannels() {
        return this.channels.filter(ch => {
            const channelHidden = this.isHidden('channel', ch.sourceId, ch.id);
            const groupHidden = this.isHidden('group', ch.sourceId, ch.groupTitle);
            return !channelHidden && !groupHidden;
        });
    }
}

// Export
window.ChannelList = ChannelList;
